import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, KeyRound } from "lucide-react";
import { api, errCode } from "../../lib/api";
import { useLang } from "../../lib/i18n";
import { useMaster, nameOf } from "../../lib/master";
import { PageTitle, Sel, Inp } from "../../components/Common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const ROLES = ["admin", "exporter", "farm_manager", "supervisor", "packing_house"];
const EMPTY = { username: "", full_name: "", role: "supervisor", grower_id: "", farm_ids: [], sector_ids: [], expires_at: "" };

const UserForm = ({ init, onSave, isNew }) => {
  const { t } = useLang();
  const growers = useMaster("growers");
  const farms = useMaster("farms");
  const sectors = useMaster("sectors");
  const [f, setF] = useState(init);
  const scoped = ["farm_manager", "supervisor"].includes(f.role);
  const toggle = (k, id) => setF({ ...f, [k]: f[k].includes(id) ? f[k].filter((x) => x !== id) : [...f[k], id] });
  return (
    <div className="space-y-3" data-testid="user-form">
      {isNew && <Inp label={t("username")} value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} testid="user-username-input" />}
      <Inp label={t("full_name")} value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} testid="user-fullname-input" />
      <Sel label={t("role")} value={f.role} onChange={(v) => setF({ ...f, role: v })} options={ROLES.map((r) => ({ id: r, name: t(`role_${r}`) }))} testid="user-role-select" />
      <Inp label={`Email (${t("optional")})`} type="email" value={f.email || ""} onChange={(e) => setF({ ...f, email: e.target.value })} testid="user-email-input" />
      <p className="text-xs text-slate-500 -mt-2">{t("user_email_hint")}</p>
      {scoped && <>
        <Sel label={t("grower")} value={f.grower_id} onChange={(v) => setF({ ...f, grower_id: v, farm_ids: [], sector_ids: [] })} options={growers} testid="user-grower-select" />
        <div><p className="eyebrow mb-1">{t("farms")}</p>{farms.filter((x) => x.grower_id === f.grower_id).map((x) => (
          <label key={x.id} className="flex items-center gap-2 py-1"><input type="checkbox" className="w-5 h-5" checked={f.farm_ids.includes(x.id)} onChange={() => toggle("farm_ids", x.id)} data-testid={`user-farm-${x.code}`} />{x.name}</label>))}</div>
        {f.role === "supervisor" && <div><p className="eyebrow mb-1">{t("sectors_opt")}</p><div className="grid grid-cols-2">{sectors.filter((s) => f.farm_ids.includes(s.farm_id)).map((s) => (
          <label key={s.id} className="flex items-center gap-2 py-1 text-sm"><input type="checkbox" checked={f.sector_ids.includes(s.id)} onChange={() => toggle("sector_ids", s.id)} />{s.code}</label>))}</div></div>}
      </>}
      {isNew && <Inp label={`${t("temp_pw_custom")} (${t("optional")})`} value={f.temp_password || ""} onChange={(e) => setF({ ...f, temp_password: e.target.value })} testid="user-temp-password-input" />}
      {isNew && <p className="text-xs text-slate-500 -mt-2">{t("temp_pw_custom_hint")}</p>}
      <Inp label={`${t("expires")} (${t("optional")})`} type="date" value={f.expires_at || ""} onChange={(e) => setF({ ...f, expires_at: e.target.value })} testid="user-expires-input" />
      <button className="btn-primary w-full" disabled={!!f.temp_password && f.temp_password.length < 8} onClick={() => onSave({ ...f, email: f.email || null, temp_password: f.temp_password || null, grower_id: scoped ? f.grower_id : null, farm_ids: scoped ? f.farm_ids : [], expires_at: f.expires_at || null })} data-testid="user-save-button">{t("save")}</button>
    </div>
  );
};

export default function Users() {
  const { t, te } = useLang();
  const growers = useMaster("growers");
  const farms = useMaster("farms");
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const [temp, setTemp] = useState(null);
  const load = () => api.get("/users").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const onErr = (e) => toast.error(te(errCode(e)));

  const save = async (f) => {
    try {
      if (edit === "new") { const { data } = await api.post("/users", f); setTemp({ user: data.user.username, pw: data.temp_password }); }
      else { const { username, ...rest } = f; await api.patch(`/users/${edit.id}`, rest); toast.success(t("saved")); }
      setEdit(null); load();
    } catch (e) { onErr(e); }
  };
  const [resetFor, setResetFor] = useState(null);
  const [resetPw, setResetPw] = useState("");
  const reset = () => api.post(`/users/${resetFor.id}/reset-password`, { temp_password: resetPw || null })
    .then((r) => { setTemp({ user: resetFor.username, pw: r.data.temp_password }); setResetFor(null); setResetPw(""); load(); }).catch(onErr);
  const toggleActive = (u) => api.patch(`/users/${u.id}`, { active: !u.active }).then(load).catch(onErr);

  return (
    <div data-testid="users-page">
      <PageTitle right={<button className="btn-primary" onClick={() => setEdit("new")} data-testid="new-user-button"><UserPlus size={18} />{t("new_user")}</button>}>{t("users_title")}</PageTitle>
      <div className="card overflow-x-auto">
        <table className="tbl w-full" data-testid="users-table">
          <thead><tr><th>{t("username")}</th><th>{t("full_name")}</th><th>{t("role")}</th><th>Email</th><th>{t("grower")} / {t("farms")}</th><th>{t("expires")}</th><th>{t("status")}</th><th></th></tr></thead>
          <tbody>{rows.map((u) => (
            <tr key={u.id} className={u.active ? "" : "opacity-50"} data-testid={`user-row-${u.username}`}>
              <td className="font-mono font-bold">{u.username}</td><td>{u.full_name}</td><td>{t(`role_${u.role}`)}</td><td className="text-xs" data-testid={`user-email-${u.username}`}>{u.email || "—"}</td>
              <td className="text-xs">{u.grower_id ? nameOf(growers, u.grower_id) : "365Fresh"}<br />{(u.farm_ids || []).map((id) => nameOf(farms, id)).join(", ")}</td>
              <td className="font-mono text-xs">{u.expires_at || "—"}</td>
              <td className="text-xs font-bold">{u.active ? t("active") : t("inactive")}{u.must_change_password ? ` · ${t("temp_pw_title")}` : ""}</td>
              <td className="flex gap-1 flex-wrap">
                <button className="btn-secondary btn-sm" onClick={() => setEdit(u)} data-testid={`edit-user-${u.username}`}>{t("edit")}</button>
                <button className="btn-secondary btn-sm" onClick={() => setResetFor(u)} data-testid={`reset-user-${u.username}`}><KeyRound size={14} />{t("reset_pw")}</button>
                <button className="btn-secondary btn-sm" onClick={() => toggleActive(u)} data-testid={`toggle-user-${u.username}`}>{u.active ? t("deactivate") : t("reactivate")}</button>
              </td></tr>))}</tbody>
        </table>
      </div>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{edit === "new" ? t("new_user") : edit?.username}</DialogTitle></DialogHeader>
          {edit && <UserForm init={edit === "new" ? EMPTY : { ...EMPTY, ...edit, grower_id: edit.grower_id || "" }} isNew={edit === "new"} onSave={save} />}
        </DialogContent>
      </Dialog>
      <Dialog open={!!resetFor} onOpenChange={(o) => !o && setResetFor(null)}>
        <DialogContent data-testid="reset-dialog">
          <DialogHeader><DialogTitle>{t("reset_pw")} · {resetFor?.username}</DialogTitle></DialogHeader>
          <Inp label={`${t("temp_pw_custom")} (${t("optional")})`} value={resetPw} onChange={(e) => setResetPw(e.target.value)} testid="reset-temp-password-input" />
          <p className="text-xs text-slate-500">{t("temp_pw_custom_hint")}</p>
          <button className="btn-primary w-full" disabled={!!resetPw && resetPw.length < 8} onClick={reset} data-testid="reset-confirm-button"><KeyRound size={16} />{t("reset_pw")}</button>
        </DialogContent>
      </Dialog>
      <Dialog open={!!temp} onOpenChange={(o) => !o && setTemp(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("temp_pw_title")}</DialogTitle></DialogHeader>
          <p className="font-mono">{temp?.user}</p>
          <p className="font-mono text-3xl font-bold bg-slate-100 p-3 rounded-xl text-center select-all" data-testid="temp-password-value">{temp?.pw}</p>
          <p className="text-sm text-slate-600">{t("temp_pw_hint")}</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
